import type { FormDocument } from '@los-workspace/workflow-runtime';
import type {
  ExistingCustomerAddressHit,
  ExistingCustomerHit,
} from '../../models/opportunity.model';
import {
  findPackFormField,
  resolvePackApiBindings,
  type PackAddressFieldMap,
  type PackApiBindings,
} from './pack-form-schema.resolver';
import { coerceText } from '../../utils/coerce-text';
import { normalizeDateForApi } from '../../utils/normalize-date-for-api.util';
import {
  pickBestCustomerAddress,
  pickBestCustomerAddressFromRecord,
  resolvePartyRecordPath,
} from '../../utils/pick-best-customer-address.util';

type DomainSemanticKey = keyof PackApiBindings | keyof PackAddressFieldMap;

const DOMAIN_TO_SEMANTIC: Record<string, DomainSemanticKey> = {
  applicantName: 'fullName',
  organizationName: 'fullName',
  contactNumber: 'mobile',
  emailId: 'email',
  emailID: 'email',
  dateOfBirth: 'date',
  dateOfIncorporation: 'date',
  genderCode: 'gender',
  gender: 'gender',
  address: 'addressLine1',
  addressLine1: 'addressLine1',
  addressLine2: 'addressLine2',
  pinCode: 'pinCode',
  city: 'city',
  district: 'district',
  stateCode: 'state',
  state: 'state',
  countryCode: 'country',
  country: 'country',
  addressTypeCode: 'addressType',
  addressType: 'addressType',
};

const SKIP_DOMAIN_KEYS = new Set(['customerId', 'applicantType', 'pan']);

function primaryAddress(hit: ExistingCustomerHit): ExistingCustomerAddressHit | undefined {
  return pickBestCustomerAddress(hit.addresses, {
    isCorporate: hit.customerType === 'C',
  });
}

/** Normalized row shape for workflow `prefillFrom: selectedRow` on create-existing nodes. */
export function mapExistingCustomerHitToSelectedRow(
  hit: ExistingCustomerHit,
): Record<string, unknown> {
  const address = primaryAddress(hit);
  return {
    id: hit.id ?? '',
    customerId: hit.customerId,
    name: hit.customerName ?? '',
    mobile: hit.mobileNumber1 ?? '',
    emailId: hit.emailId ?? '',
    genderCode: hit.genderCode ?? '',
    address: address?.addressLine1 ?? hit.address?.trim() ?? '',
    addressLine1: address?.addressLine1 ?? hit.address?.trim() ?? '',
    addressLine2: address?.addressLine2 ?? '',
    pinCode: address?.pinCode ?? '',
    city: address?.city ?? '',
    district: address?.district ?? '',
    stateCode: address?.stateCode ?? '',
    countryCode: address?.countryCode ?? '',
    addressTypeCode: address?.addressTypeCode ?? '',
    pan: hit.panNumber ?? '',
    dob: hit.customerDob ?? '',
    dateOfIncorporation: hit.dateOfInCorporation ?? '',
    type: hit.customerType === 'C' ? 'Corporate' : 'Individual',
  };
}

/** Infer UI applicant type from GET /parties/:id record. */
export function inferApplicantTypeFromPartyRecord(
  record: Record<string, unknown>,
): 'corporate' | 'individual' {
  const customerType = coerceText(record['customerType'] ?? record['partyType'] ?? '')
    .trim()
    .toUpperCase();
  if (customerType === 'C' || customerType.includes('CORP')) {
    return 'corporate';
  }
  if (customerType === 'I' || customerType.includes('IND')) {
    return 'individual';
  }

  const customerTypeDesc = coerceText(record['customerTypeDesc'] ?? '').toLowerCase();
  if (customerTypeDesc.includes('corp')) {
    return 'corporate';
  }
  return 'individual';
}

/** Resolve LMS customer id from a party record for create/save payloads. */
export function inferLmsCustomerIdFromPartyRecord(
  record: Record<string, unknown>,
): string | undefined {
  return resolveLmsCustomerIdForSave({
    customerId: record['customerId'] ?? record['lmsCustomerId'],
    partyId: record['id'] ?? record['partyId'],
  });
}

/** Build a selectedRow-like object from URL/query fallbacks when workflow state is absent. */
export function buildSelectedRowFromRouteContext(args: {
  partyId?: string | null;
  customerId?: string | null;
  customerName?: string | null;
  contact?: string | null;
  emailId?: string | null;
  genderCode?: string | null;
  dob?: string | null;
  dateOfIncorporation?: string | null;
  pan?: string | null;
  address?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  pinCode?: string | null;
  city?: string | null;
  district?: string | null;
  stateCode?: string | null;
  countryCode?: string | null;
  addressTypeCode?: string | null;
  applicantType?: string | null;
}): Record<string, unknown> | null {
  if (!args.customerId?.trim()) {
    return null;
  }

  const isCorporate = coerceText(args.applicantType).toLowerCase() === 'corporate';
  const line1 = args.addressLine1?.trim() ?? args.address?.trim() ?? '';
  return {
    id: args.partyId?.trim() ?? '',
    customerId: args.customerId.trim(),
    name: args.customerName?.trim() ?? '',
    mobile: args.contact?.trim() ?? '',
    emailId: args.emailId?.trim() ?? '',
    genderCode: args.genderCode?.trim() ?? '',
    dob: args.dob?.trim() ?? '',
    dateOfIncorporation: args.dateOfIncorporation?.trim() ?? args.dob?.trim() ?? '',
    pan: args.pan?.trim() ?? '',
    address: line1,
    addressLine1: line1,
    addressLine2: args.addressLine2?.trim() ?? '',
    pinCode: args.pinCode?.trim() ?? '',
    city: args.city?.trim() ?? '',
    district: args.district?.trim() ?? '',
    stateCode: args.stateCode?.trim() ?? '',
    countryCode: args.countryCode?.trim() ?? '',
    addressTypeCode: args.addressTypeCode?.trim() ?? '',
    type: isCorporate ? 'Corporate' : 'Individual',
  };
}

/**
 * Maps workflow node `fieldMapping` (domain field → source row key) into pack form
 * `initialValues` using bindings resolved from the active FormDocument.
 */
export function mapWorkflowFieldMappingToPackValues(
  formDoc: FormDocument | null | undefined,
  fieldMapping: Record<string, string>,
  sourceRow: Record<string, unknown>,
): Record<string, unknown> {
  const bindings = resolvePackApiBindings(formDoc);
  const values: Record<string, unknown> = {};

  for (const [domainKey, sourcePath] of Object.entries(fieldMapping)) {
    if (SKIP_DOMAIN_KEYS.has(domainKey)) {
      continue;
    }

    const raw = resolveSourceRowValue(sourceRow, sourcePath);
    if (isEmptyPrefillValue(raw)) {
      continue;
    }

    const semantic = DOMAIN_TO_SEMANTIC[domainKey];
    if (!semantic) {
      continue;
    }

    const packFieldCode = resolvePackFieldCode(bindings, semantic);
    if (!packFieldCode) {
      continue;
    }

    values[packFieldCode] = normalizePackFieldValue(semantic, raw);
  }

  return values;
}

const BASIC_DETAIL_KEYS = new Set([
  'applicantName',
  'organizationName',
  'contactNumber',
  'emailId',
  'emailID',
  'dateOfBirth',
  'dateOfIncorporation',
  'gender',
]);

const ADDRESS_DETAIL_KEYS = new Set([
  'addressLine1',
  'addressLine2',
  'pinCode',
  'city',
  'district',
  'state',
  'country',
  'addressType',
]);

/** Resolve LOS party UUID for GET /parties/:id proxy calls. */
export function resolvePartyIdForProxy(
  source: Record<string, unknown> | null | undefined,
): string | null {
  if (!source) {
    return null;
  }
  const partyId = coerceText(source['id'] ?? source['partyId'] ?? '').trim();
  if (partyId && isUuidLike(partyId)) {
    return partyId;
  }
  const customerId = coerceText(source['customerId'] ?? '').trim();
  if (customerId && isUuidLike(customerId)) {
    return customerId;
  }
  return partyId || null;
}

/**
 * UUID for create payload `lmsCustomerId` when applicantStatus is EXISTING.
 * Search returns `customerId` as LMS UUID or CUST-xxx display id; `partyId`/`id` is the LOS party UUID.
 */
export function resolveLmsCustomerIdForSave(
  source: Record<string, unknown> | null | undefined,
): string | undefined {
  if (!source) {
    return undefined;
  }
  const customerId = coerceText(
    source['customerId'] ?? source['lmsCustomerId'] ?? '',
  ).trim();
  if (customerId && isUuidLike(customerId)) {
    return customerId;
  }
  const partyId = coerceText(source['partyId'] ?? source['id'] ?? '').trim();
  if (partyId && isUuidLike(partyId)) {
    return partyId;
  }
  return undefined;
}

/** Map GET /parties/:id record + workflow responseFieldMapping → create form patches. */
export function mapPartyRecordToCreateFormPatch(
  record: Record<string, unknown>,
  responseFieldMapping: Record<string, string>,
  applicantType?: string | null,
): {
  basicDetails: Record<string, unknown>;
  addressDetails: Record<string, unknown>;
} {
  const isCorporate = coerceText(applicantType).toLowerCase() === 'corporate';
  const basicDetails: Record<string, unknown> = {};
  const addressDetails: Record<string, unknown> = {};

  for (const [domainKey, recordPath] of Object.entries(responseFieldMapping)) {
    const raw = resolvePartyRecordPath(record, recordPath, { isCorporate });
    if (isEmptyPrefillValue(raw)) {
      continue;
    }

    const normalized = normalizeCreateFormDomainValue(domainKey, raw, isCorporate);
    if (normalized === undefined || normalized === '') {
      continue;
    }

    const targetKey = domainKey === 'emailID' ? 'emailId' : domainKey;
    if (BASIC_DETAIL_KEYS.has(domainKey) || BASIC_DETAIL_KEYS.has(targetKey)) {
      basicDetails[targetKey] = normalized;
      continue;
    }
    if (ADDRESS_DETAIL_KEYS.has(domainKey)) {
      addressDetails[targetKey] = normalized;
    }
  }

  return { basicDetails, addressDetails };
}

function primaryAddressFromRecord(
  record: Record<string, unknown>,
  applicantType?: string | null,
): ExistingCustomerAddressHit | undefined {
  const isCorporate = coerceText(applicantType).toLowerCase() === 'corporate';
  return pickBestCustomerAddressFromRecord(record, { isCorporate });
}

function str(value: unknown): string | undefined {
  const trimmed = coerceText(value ?? '').trim();
  return trimmed || undefined;
}

/** Standard party API record → Angular basic/address create forms (individual + corporate). */
export function mapPartyRecordToStandardCreateFormPatch(
  record: Record<string, unknown>,
  applicantType: string | null | undefined,
): {
  basicDetails: Record<string, unknown>;
  addressDetails: Record<string, unknown>;
} {
  const isCorporate = coerceText(applicantType).toLowerCase() === 'corporate';
  const address = primaryAddressFromRecord(record, applicantType);
  const basicDetails = isCorporate
    ? compactRecord({
        organizationName: coerceText(record['customerName'] ?? ''),
        contactNumber: normalizePackMobile(record['mobileNumber1']),
        emailId: coerceText(
          record['emailId'] ?? record['email'] ?? record['emailID'] ?? '',
        ),
        dateOfIncorporation: normalizePackDateValue(
          record['dateOfIncorporation'] ?? record['dateOfInCorporation'],
        ),
      })
    : compactRecord({
        applicantName: coerceText(record['customerName'] ?? ''),
        contactNumber: normalizePackMobile(record['mobileNumber1']),
        emailId: coerceText(
          record['emailId'] ?? record['email'] ?? record['emailID'] ?? '',
        ),
        dateOfBirth: normalizePackDateValue(record['customerDob']),
        gender: normalizePackGenderValueForBasicForm(
          record['genderCode'] ?? record['gender'],
        ),
      });

  const addressDetails = address
    ? compactRecord({
        addressLine1: address.addressLine1,
        addressLine2: address.addressLine2 ?? '',
        pinCode: address.pinCode,
        city: address.city,
        district: address.district,
        state: address.stateCode,
        country: address.countryCode,
        addressType: normalizePackAddressTypeValueForBasicForm(
          address.addressTypeCode,
          isCorporate,
        ),
      })
    : {};

  return { basicDetails, addressDetails };
}

function compactRecord(
  values: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value === undefined || value === null || value === '') {
      continue;
    }
    out[key] = value;
  }
  return out;
}

function normalizePackAddressTypeValueForBasicForm(
  value: unknown,
  isCorporate: boolean,
): string {
  const normalized = coerceText(value).trim().toLowerCase();
  if (isCorporate) {
    if (normalized.includes('registered')) return 'registered office';
    if (normalized.includes('head')) return 'head office';
    return normalized || 'registered office';
  }
  if (normalized.includes('permanent')) return 'permanent';
  if (normalized.includes('current')) return 'current';
  return normalized || 'permanent';
}

function isPackFieldResponseMapping(mapping: Record<string, string>): boolean {
  return Object.keys(mapping).some(
    (key) =>
      !BASIC_DETAIL_KEYS.has(key) &&
      !ADDRESS_DETAIL_KEYS.has(key) &&
      !DOMAIN_TO_SEMANTIC[key],
  );
}

/** Map party record paths directly onto pack field codes (corporate load-form nodes). */
export function mapPartyRecordToPackFieldValues(
  record: Record<string, unknown>,
  responseFieldMapping: Record<string, string>,
): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const [fieldCode, recordPath] of Object.entries(responseFieldMapping)) {
    const raw = resolvePartyRecordFieldValue(record, recordPath);
    if (isEmptyPrefillValue(raw)) {
      continue;
    }
    values[fieldCode] = normalizePackFieldCodeValue(fieldCode, raw);
  }
  return values;
}

/** Map domain basic/address patches onto the active pack form field codes. */
export function mapBasicAddressDetailsToPackValues(
  formDoc: FormDocument | null | undefined,
  basicDetails: Record<string, unknown>,
  addressDetails: Record<string, unknown>,
): Record<string, unknown> {
  const bindings = resolvePackApiBindings(formDoc);
  const values: Record<string, unknown> = {};

  const assign = (fieldCode: string | undefined, value: unknown): void => {
    if (!fieldCode || isEmptyPrefillValue(value)) {
      return;
    }
    values[fieldCode] = value;
  };

  assign(
    bindings.fullName,
    basicDetails['organizationName'] ?? basicDetails['applicantName'],
  );
  assign(bindings.mobile, basicDetails['contactNumber']);
  assign(bindings.email, basicDetails['emailId'] ?? basicDetails['emailID']);
  assign(
    bindings.date,
    basicDetails['dateOfIncorporation'] ?? basicDetails['dateOfBirth'],
  );
  assign(
    bindings.gender,
    basicDetails['gender'] != null && basicDetails['gender'] !== ''
      ? normalizePackGenderValue(basicDetails['gender'])
      : undefined,
  );

  const addressMap = bindings.address;
  assign(addressMap.addressLine1, addressDetails['addressLine1']);
  assign(addressMap.addressLine2, addressDetails['addressLine2']);
  assign(addressMap.pinCode, addressDetails['pinCode']);
  assign(addressMap.city, addressDetails['city']);
  assign(addressMap.district, addressDetails['district']);
  assign(addressMap.state, addressDetails['state'] ?? addressDetails['stateCode']);
  assign(addressMap.country, addressDetails['country'] ?? addressDetails['countryCode']);
  assign(
    addressMap.addressType,
    addressDetails['addressType'] ?? addressDetails['addressTypeCode'],
  );

  return values;
}

/**
 * Workflow corporate-existing nodes still map to legacy pack codes (`textField`, `phone`, …).
 * Super-admin forms bind semantic codes (`organizationName`, `contactNumber`, …).
 */
export function remapLegacyPackFieldValuesToFormDoc(
  formDoc: FormDocument | null | undefined,
  values: Record<string, unknown>,
): Record<string, unknown> {
  const bindings = resolvePackApiBindings(formDoc);
  const out: Record<string, unknown> = {};

  const legacyBasic: Record<string, string | undefined> = {
    textField: bindings.fullName,
    phone: bindings.mobile,
    email: bindings.email,
    date: bindings.date,
  };
  const legacyAddress: Record<string, keyof PackAddressFieldMap> = {
    textField2: 'addressLine1',
    textField3: 'addressLine2',
    number2: 'pinCode',
    textField4: 'city',
    textField5: 'district',
    textField6: 'state',
    textField7: 'country',
    radio: 'addressType',
  };

  for (const [legacyKey, value] of Object.entries(values)) {
    if (isEmptyPrefillValue(value)) {
      continue;
    }

    if (findPackFormField(formDoc, legacyKey)) {
      out[legacyKey] = value;
      continue;
    }

    const basicTarget = legacyBasic[legacyKey];
    if (basicTarget) {
      out[basicTarget] = value;
      continue;
    }

    const addressSemantic = legacyAddress[legacyKey];
    if (addressSemantic) {
      const target = bindings.address[addressSemantic];
      if (target) {
        out[target] = value;
        continue;
      }
    }

    if (legacyKey === 'radio' && bindings.gender) {
      out[bindings.gender] = value;
    }
  }

  return out;
}

function resolvePartyRecordFieldValue(
  record: Record<string, unknown>,
  path: string,
): unknown {
  const isCorporate =
    inferApplicantTypeFromPartyRecord(record) === 'corporate';
  const direct = resolvePartyRecordPath(record, path, { isCorporate });
  if (!isEmptyPrefillValue(direct)) {
    return direct;
  }

  const trimmed = path.trim();
  if (trimmed === 'dateOfInCorporation' || trimmed === 'dateOfIncorporation') {
    return resolvePartyRecordPath(record, 'dateOfIncorporation', {
      isCorporate,
    }) ?? resolvePartyRecordPath(record, 'dateOfInCorporation', { isCorporate });
  }

  return direct;
}

/** Unified prefill for individual (domain mapping) and corporate (pack field mapping). */
export function applyExistingCustomerPartyPrefill(args: {
  record: Record<string, unknown>;
  responseFieldMapping: Record<string, string>;
  formDoc?: FormDocument | null;
  applicantType?: string | null;
}): {
  basicDetails: Record<string, unknown>;
  addressDetails: Record<string, unknown>;
  packValues: Record<string, unknown>;
} {
  const standardPatch = mapPartyRecordToStandardCreateFormPatch(
    args.record,
    args.applicantType ?? null,
  );
  const mappedPatch = isPackFieldResponseMapping(args.responseFieldMapping)
    ? { basicDetails: {}, addressDetails: {} }
    : mapPartyRecordToCreateFormPatch(
        args.record,
        args.responseFieldMapping,
        args.applicantType ?? null,
      );

  const basicDetails = {
    ...mappedPatch.basicDetails,
    ...standardPatch.basicDetails,
  };
  const addressDetails = {
    ...mappedPatch.addressDetails,
    ...standardPatch.addressDetails,
  };

  const mappedPackValues = isPackFieldResponseMapping(args.responseFieldMapping)
    ? remapLegacyPackFieldValuesToFormDoc(
        args.formDoc,
        mapPartyRecordToPackFieldValues(args.record, args.responseFieldMapping),
      )
    : mapWorkflowFieldMappingToPackValues(
        args.formDoc,
        args.responseFieldMapping,
        args.record,
      );

  const packValues = {
    ...mappedPackValues,
    ...mapBasicAddressDetailsToPackValues(
      args.formDoc,
      basicDetails,
      addressDetails,
    ),
  };

  return { basicDetails, addressDetails, packValues };
}

function normalizePackFieldCodeValue(fieldCode: string, value: unknown): unknown {
  if (fieldCode === 'phone') {
    return normalizePackMobile(value);
  }
  if (fieldCode === 'date') {
    return normalizePackDateValue(value);
  }
  if (fieldCode === 'radio') {
    return normalizePackAddressTypeValue(value);
  }
  if (fieldCode === 'number2') {
    const pin = coerceText(value).trim();
    if (!pin) return '';
    const asNumber = Number.parseInt(pin, 10);
    return Number.isFinite(asNumber) ? asNumber : pin;
  }
  return coerceText(value).trim();
}

function normalizeCreateFormDomainValue(
  domainKey: string,
  value: unknown,
  isCorporate = false,
): unknown {
  if (domainKey === 'contactNumber') {
    return normalizePackMobile(value);
  }
  if (domainKey === 'dateOfBirth' || domainKey === 'dateOfIncorporation') {
    return normalizePackDateValue(value);
  }
  if (domainKey === 'gender' || domainKey === 'genderCode') {
    return normalizePackGenderValueForBasicForm(value);
  }
  if (domainKey === 'addressType' || domainKey === 'addressTypeCode') {
    return normalizePackAddressTypeValueForBasicForm(value, isCorporate);
  }
  if (domainKey === 'pinCode') {
    const pin = coerceText(value).trim();
    if (!pin) return '';
    const asNumber = Number.parseInt(pin, 10);
    return Number.isFinite(asNumber) ? asNumber : pin;
  }
  return coerceText(value).trim();
}

function normalizePackGenderValueForBasicForm(value: unknown): string {
  const normalized = coerceText(value).trim().toLowerCase();
  if (normalized === 'male' || normalized === 'm' || normalized.startsWith('male')) {
    return 'male';
  }
  if (normalized === 'female' || normalized === 'f' || normalized.startsWith('female')) {
    return 'female';
  }
  if (normalized === 'other' || normalized === 'o' || normalized.startsWith('other')) {
    return 'other';
  }
  return normalized;
}

function isUuidLike(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );
}

function resolvePackFieldCode(
  bindings: PackApiBindings,
  semantic: DomainSemanticKey,
): string | undefined {
  if (semantic in bindings.address) {
    return bindings.address[semantic as keyof PackAddressFieldMap];
  }
  const code = bindings[semantic as keyof Omit<PackApiBindings, 'address'>];
  return typeof code === 'string' ? code : undefined;
}

function resolveSourceRowValue(
  row: Record<string, unknown>,
  path: string,
): unknown {
  const trimmed = path.trim();
  if (!trimmed) {
    return undefined;
  }

  if (!trimmed.includes('.')) {
    return row[trimmed];
  }

  let current: unknown = row;
  for (const part of trimmed.split('.').filter(Boolean)) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

function isEmptyPrefillValue(value: unknown): boolean {
  if (value == null) {
    return true;
  }
  const text = coerceText(value).trim();
  return !text || text === '—';
}

function normalizePackFieldValue(
  semantic: DomainSemanticKey,
  value: unknown,
): unknown {
  if (semantic === 'mobile') {
    return normalizePackMobile(value);
  }
  if (semantic === 'date') {
    return normalizePackDateValue(value);
  }
  if (semantic === 'gender') {
    return normalizePackGenderValue(value);
  }
  if (semantic === 'addressType') {
    return normalizePackAddressTypeValue(value);
  }
  if (semantic === 'pinCode') {
    const pin = coerceText(value).trim();
    if (!pin) return '';
    const asNumber = Number.parseInt(pin, 10);
    return Number.isFinite(asNumber) ? asNumber : pin;
  }
  return coerceText(value).trim();
}

function normalizePackMobile(value: unknown): string {
  const text = coerceText(value).trim();
  if (!text || text === '—') {
    return '';
  }
  const digits = text.replace(/\D/g, '');
  return digits.slice(-10) || text;
}

function normalizePackGenderValue(value: unknown): string {
  const normalized = coerceText(value).trim().toLowerCase();
  if (normalized === 'male' || normalized === 'm' || normalized.startsWith('male')) {
    return 'Male';
  }
  if (normalized === 'female' || normalized === 'f' || normalized.startsWith('female')) {
    return 'Female';
  }
  if (normalized === 'other' || normalized === 'o' || normalized.startsWith('other')) {
    return 'Other';
  }
  return coerceText(value).trim();
}

function normalizePackAddressTypeValue(value: unknown): string {
  const normalized = coerceText(value).trim().toLowerCase();
  if (normalized.includes('permanent')) return 'Permanent';
  if (normalized.includes('current')) return 'Current';
  if (normalized.includes('registered')) return 'Registered Office';
  if (normalized.includes('head')) return 'Head Office';
  return coerceText(value).trim();
}

/** Accepts DD-MM-YYYY, DD/MM/YYYY, ISO, and other Date-parseable strings for HTML date inputs. */
export function normalizePackDateValue(value: unknown): string {
  return normalizeDateForApi(value) ?? '';
}
